import createServiceStore from 'shared/utils/createServiceStore.js';
import { SOURCES_SERVICE_KEY } from './sourcesEnum.js';
import sourcesService from './sourcesService.js';
import { setLoading } from '../loading/loadingMutationTypes.js';
import { setError } from '../error/errorMutationTypes.js';
import { forEach, get, last, capitalize, isArray } from 'lodash';
import {
  changeSourceStatusAction,
  testExistingSourceConnectionAction,
  testSourceConnectionAction,
  getGA4PropertiesAction,
  getFacebookPagesAction,
  getSchemaAction,
} from 'web/sources/sourcesActionTypes';
import { setSourceTestConnectionResult } from 'web/sources/sourcesMutationTypes';
import sourcesServiceActions from 'web/sources/sourcesServiceActions';
import { sourceKeysMappings } from './sourceKeysMappings.js';

export default createServiceStore({
  serviceName: SOURCES_SERVICE_KEY,
  service: sourcesService,
  setLoadingMutation: setLoading,
  setErrorMutation: setError,
  actions: {
    async [testSourceConnectionAction]({ commit }, { data, config } = {}) {
      commit(setLoading, { key: testSourceConnectionAction, value: true });
      commit(setError, { key: testSourceConnectionAction, value: null });
      commit(setError, { key: sourcesServiceActions.create, value: null });
      commit(setSourceTestConnectionResult, {});

      try {
        const result = await sourcesService.testConnection({
          data: { ...data, data_store: data.data_store },
          ...config,
        });
        commit(setSourceTestConnectionResult, result);
      } catch (err) {
        //Handle 422 errors that are due to schema validation errors
        if (get(err, 'response.status') === 422) {
          let schemaErrors = [];
          const sourceType = get(data, 'config.source_type', '');
          if (!sourceType) {
            schemaErrors.push({
              success: false,
              action: 'Check Source Configuration',
              message:
                'Please verify the source configuration, and make sure all required fields are filled',
              mandatory: true,
            });
          } else {
            const errorMessages = get(
              err,
              'response.data.detail',
              'Invalid credentials',
            );
            if (isArray(errorMessages)) {
              forEach(errorMessages, (errorMessage) => {
                const configKey = last(get(errorMessage, 'loc', []));
                const configKeyLabel = get(sourceKeysMappings, [
                  sourceType,
                  configKey,
                ]);
                if (configKeyLabel) {
                  const message = capitalize(get(errorMessage, 'msg', ''));
                  schemaErrors.push({
                    success: false,
                    action: `Verify "${configKeyLabel}" Field`,
                    message: message,
                    mandatory: true,
                  });
                }
              });
            } else {
              schemaErrors.push({
                success: false,
                action: 'Check Source Configuration',
                message: 'Invalid credentials',
                mandatory: true,
              });
            }
          }
          commit(setSourceTestConnectionResult, {
            results: schemaErrors,
            success: false,
          });
        } else {
          commit(setError, { key: testSourceConnectionAction, value: err });
          throw err;
        }
      } finally {
        commit(setLoading, { key: testSourceConnectionAction, value: false });
      }
    },
    async [testExistingSourceConnectionAction](
      { commit },
      { id, data = {} } = {},
    ) {
      commit(setLoading, {
        key: testExistingSourceConnectionAction,
        value: true,
      });
      commit(setError, {
        key: testExistingSourceConnectionAction,
        value: null,
      });
      commit(setError, { key: sourcesServiceActions.create, value: null });
      commit(setSourceTestConnectionResult, {});

      try {
        const result = await sourcesService.testExistingSourceConnection(id, {
          data: {
            config: data.config,
            ...(data.tunnel_id ? { tunnel_id: data.tunnel_id } : {}),
          },
        });
        commit(setSourceTestConnectionResult, result);
      } catch (err) {
        //Handle 422 errors that are due to schema validation errors
        if (get(err, 'response.status') === 422) {
          let schemaErrors = [];
          const sourceType = get(data, 'config.source_type', '');
          if (!sourceType) {
            schemaErrors.push({
              success: false,
              action: 'Check Source Configuration',
              message:
                'Please verify the source configuration, and make sure all required fields are filled',
              mandatory: true,
            });
          } else {
            const errorMessages = get(
              err,
              'response.data.detail',
              'Invalid credentials',
            );
            if (isArray(errorMessages)) {
              forEach(errorMessages, (errorMessage) => {
                const configKey = last(get(errorMessage, 'loc', []));
                const configKeyLabel = get(sourceKeysMappings, [
                  sourceType,
                  configKey,
                ]);
                if (configKeyLabel) {
                  const message = capitalize(get(errorMessage, 'msg', ''));
                  schemaErrors.push({
                    success: false,
                    action: `Verify "${configKeyLabel}" Field`,
                    message: message,
                    mandatory: true,
                  });
                }
              });
            } else {
              schemaErrors.push({
                success: false,
                action: 'Check Source Configuration',
                message: 'Invalid credentials',
                mandatory: true,
              });
            }
          }
          commit(setSourceTestConnectionResult, {
            results: schemaErrors,
            success: false,
          });
        } else {
          commit(setError, {
            key: testExistingSourceConnectionAction,
            value: err,
          });
          throw err;
        }
      } finally {
        commit(setLoading, {
          key: testExistingSourceConnectionAction,
          value: false,
        });
      }
    },
    async [changeSourceStatusAction](
      { commit },
      { sourceId, status, silent = false },
    ) {
      !silent &&
        commit(setLoading, { key: changeSourceStatusAction, value: status });
      commit(setError, { key: changeSourceStatusAction, value: null });

      try {
        await sourcesService.changeStatus(sourceId, status);
      } catch (err) {
        commit(setError, { key: changeSourceStatusAction, value: err });
        throw err;
      } finally {
        !silent &&
          commit(setLoading, { key: changeSourceStatusAction, value: false });
      }
    },
    async [getGA4PropertiesAction]({ commit }, { accessToken, refreshToken }) {
      commit(setLoading, { key: getGA4PropertiesAction, value: true });
      commit(setError, { key: getGA4PropertiesAction, value: null });
      try {
        return await sourcesService.getGA4Properties({
          data: { access_token: accessToken, refresh_token: refreshToken },
        });
      } catch (err) {
        commit(setError, { key: getGA4PropertiesAction, value: err });
        throw err;
      } finally {
        commit(setLoading, { key: getGA4PropertiesAction, value: false });
      }
    },
    async [getFacebookPagesAction]({ commit }, { accessToken }) {
      commit(setLoading, { key: getFacebookPagesAction, value: true });
      commit(setError, { key: getFacebookPagesAction, value: null });
      try {
        return await sourcesService.getFacebookPages({
          data: { access_token: accessToken },
        });
      } catch (err) {
        commit(setError, { key: getFacebookPagesAction, value: err });
        throw err;
      } finally {
        commit(setLoading, { key: getFacebookPagesAction, value: false });
      }
    },
    async [getSchemaAction](
      { commit },
      { config, data_store, sample_options },
    ) {
      commit(setLoading, { key: getSchemaAction, value: true });
      commit(setError, { key: getSchemaAction, value: null });
      let result = null;
      try {
        result = await sourcesService.getSchema({
          data: {
            config: config,
            data_store: data_store,
            sample_options: sample_options,
          },
        });
        if (result.success) {
          return result;
        } else {
          throw new Error(result.error);
        }
      } catch (err) {
        commit(setError, { key: getSchemaAction, value: err });
        throw err;
      } finally {
        commit(setLoading, { key: getSchemaAction, value: false });
      }
    },
  },
});
